import React, { ReactElement, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GridFilterCellProps } from '@progress/kendo-react-grid';
import { DatePicker } from '@components-clean/core/date-picker';
import { DateTimePicker } from '@components-clean/core/datetime-picker';
import { DropDownList } from '@components-clean/core/drop-down-list';
import NumericSearchBox from '@components-clean/core/grid/grid-colum-search/numeric-search-box';
import { SearchBox } from '@components-clean/core/search-box';
import { booleanValues } from '@components-clean/core/study-data-filter/advanced-select/advanced-select.utils';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromLocal } from '@utils/convertor';

interface GridColumSearchProps {
    value: string | boolean | null;
    onChange: (value: string | boolean | null) => void;
    type: GridFilterCellProps['filterType'] | 'date-time' | 'float';
    disabled?: boolean;
    clientGrid?: boolean;
    lovData?: string[];
    showPlaceholder?: boolean;
}

function convertBoolString(value: string | boolean | null, stringEq: true): string;
function convertBoolString(value: string | boolean | null, stringEq?: false): boolean;

function convertBoolString(value: string | boolean | null, stringEq?: boolean): string | boolean {
    switch (value) {
        case 'true':
        case true:
            return stringEq ? 'yes' : true;
        case 'false':
        case false:
            return stringEq ? 'no' : false;
        default:
            return '';
    }
}

const GridColumSearch = ({
    value,
    onChange,
    type,
    disabled,
    clientGrid,
    lovData,
    showPlaceholder,
}: GridColumSearchProps) => {
    const { t } = useTranslation();
    let defaultDate: Date | null = null;
    if (value && typeof value === 'string' && (type === 'date' || type === 'date-time')) {
        defaultDate = new Date(value);
    }
    const [date, setDate] = useState<Date | null>(defaultDate);
    const [debounceValue, setDebounceValue] = useState<string | null | undefined>(undefined);

    if (showPlaceholder === undefined) {
        showPlaceholder = true;
    }

    useEffect(() => {
        if (value == null) {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            /* eslint-disable @eslint-react/set-state-in-effect */
            setDate(null);
            setDebounceValue(undefined);
            /* eslint-enable @eslint-react/set-state-in-effect */
        }
    }, [value]);

    useEffect(() => {
        if (value && typeof value !== 'object' && clientGrid) {
            onChange(value);
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    useEffect(() => {
        if (debounceValue !== undefined) {
            const timeout = setTimeout(() => {
                onChange(debounceValue);
            }, 500);

            return () => {
                clearTimeout(timeout);
            };
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [debounceValue]);

    const noDataRender = (divProps: ReactElement<HTMLDivElement>) => {
        const children = [
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/no-missing-key
            <div className="k-d-flex k-d-flex-row k-justify-content-center k-align-items-center">
                <FontAwesomeIcon data-testid="no-data-found-icon" icon={['far', 'close']} fontSize={20} />
                <p className="k-pl-2 k-my-0">{t('cm2NoResults')}</p>
            </div>,
        ];

        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/no-clone-element
        return React.cloneElement(divProps, divProps.props, children);
    };

    const getComponentForFilter = () => {
        if (lovData !== undefined) {
            return (
                <DropDownList
                    data-testid="column-lov-search"
                    data={lovData}
                    clearButton={!!value}
                    value={value as string}
                    onChange={(e) => {
                        const ddValue = (e.target.value as string) ?? '';
                        onChange(ddValue);
                    }}
                    fillMode="flat"
                    className="column-search"
                    disabled={disabled}
                    showPlaceholder={showPlaceholder}
                    popupSettings={{
                        popupClass: 'k-dropdownlist-popup',
                    }}
                    listNoDataRender={noDataRender}
                />
            );
        }

        switch (type) {
            case 'boolean':
                const label = convertBoolString(value, true);
                return (
                    <DropDownList
                        data-testid="column-boolean-search"
                        data={booleanValues.map((x) => ({
                            value: x.value,
                            label: t(x.label),
                        }))}
                        textField="label"
                        clearButton={value != null}
                        value={{
                            value: convertBoolString(value),
                            label: label ? t(label) : '',
                        }}
                        onChange={(e) => {
                            const ddValue = e.target.value as { value: boolean; label: string } | null;

                            if (clientGrid) onChange(ddValue?.value ?? '');
                            else onChange(String(ddValue?.value ?? ''));
                        }}
                        fillMode="flat"
                        className="column-search"
                        disabled={disabled}
                        showPlaceholder={showPlaceholder}
                    />
                );
            case 'date':
                return (
                    <div className="k-flex-grow">
                        <DatePicker
                            data-testid="column-date-search"
                            value={date}
                            fillMode="flat"
                            onChange={(e) => {
                                setDate(e.value);
                                onChange(stringDateWithoutTimeFromLocal(e.value as Date) ?? '');
                            }}
                            className="column-search"
                            disabled={disabled}
                            columnSearch
                            showPlaceholder={showPlaceholder}
                        />
                    </div>
                );
            case 'date-time':
                return (
                    <div className="k-flex-grow">
                        <DateTimePicker
                            data-testid="column-date-search"
                            value={date}
                            fillMode="flat"
                            onChange={(e) => {
                                setDate(e.value);
                                onChange(stringDateWithoutTimeFromLocal(e.value as Date) ?? '');
                            }}
                            className="column-search"
                            disabled={disabled}
                            showPlaceholder={showPlaceholder}
                        />
                    </div>
                );
            case 'numeric':
            case 'float':
                return (
                    <div className="k-flex-grow">
                        <NumericSearchBox
                            data-testid="column-number-search"
                            placeholder=""
                            fillMode="flat"
                            value={value ? Number(value) : null}
                            onChange={(e) => {
                                setDebounceValue(e.value?.toString() ?? '');
                            }}
                            className="column-search"
                            disabled={disabled}
                            spinners={false}
                            format="0.##"
                        />
                    </div>
                );
            default:
                return (
                    <SearchBox
                        data-testid="column-search-box"
                        fillMode="flat"
                        placeholder=""
                        value={value ? String(value) : ''}
                        onChange={(e) => {
                            setDebounceValue(
                                Array.isArray(e.value) ? (e.value as string[])[0] : e.value ? e.value.toString() : '',
                            );
                        }}
                        className="column-search"
                        disabled={disabled}
                    />
                );
        }
    };

    return getComponentForFilter();
};

export default GridColumSearch;
